/**
 * Canonical onboarding exception catalog (ENG-4308 / MSG).
 *
 * One entry per NAMED failure either noise filter can classify, keyed by the
 * `studio:<subsystem>:<issue>` fingerprint those filters already assign. Each
 * entry carries the plain-English what/why plus the severity and blocking tags,
 * so Product can read a failure in Error Tracking without a stack trace.
 *
 * Not every row can reach PostHog: a suppression rule drops its match before the
 * send boundary. `delivery` declares which side of that line a row is on, so a
 * consumer building a metric from this catalog can tell a signal it will actually
 * receive from one the app never forwards.
 *
 * This module is the SINGLE source for those strings and tags. Both exception
 * pipelines consume it — the renderer filter (frontend `posthog/noise-filter.ts`,
 * via the `analytics/constants/exception-catalog.ts` re-export) and the Electron
 * main filter (`studio/electron/src/main/posthog-noise-filter.ts`) — and so do
 * their unit tests and the e2e receipt.
 *
 * It lives in studio/shared because electron-vite transforms every file with its
 * NEAREST tsconfig: under studio/client/packages/frontend it would inherit
 * `extends: "@tools/tsconfig/react"`, which the Electron-only CI install cannot
 * resolve (see ../tsconfig.json). Deliberately dependency-free for the same
 * reason, and so it never pulls anything browser-only into the main process.
 *
 * Per rule: code-comments-required (ai.knowledge/global)
 * Per rule: reference-rules-in-comments (ai.knowledge/global)
 * Per rule: think-in-systems (ai.knowledge/global) — one derivation point.
 */

/**
 * Severity classifications stamped on canonical events (ENG-4340 map) and on
 * named exceptions (ENG-4308 catalog). One shared vocabulary so an alert rule
 * filters the same way whichever side the signal came from; the three tiers are
 * the ENG-4300 stage-map shorthand. The renderer re-exports it from
 * analytics/constants/event-properties.ts.
 *
 * S1/fatal — blocks the happy path hard (app will not boot). Always alert.
 * S2/error — a real failure worth alerting on where it blocks a stage.
 * S3/warn  — EXPECTED, non-failure signal. Consumers (alert rules, dashboards)
 *   MUST exclude S3/warn from blocking-spike alert semantics: for
 *   discovery_empty, "no cameras found yet" is a normal early-journey state,
 *   not an incident, and the suppressed teardown exceptions are routine.
 *
 * Typed const (not a call-site literal) per the ENG-4340 typed-name contract
 * and rule: reference-rules-in-comments (ai.knowledge/global).
 */
export const EVENT_SEVERITY = {
  S1_FATAL: 'S1/fatal',
  // S2/error is a REAL operational failure (ENG-4323 device_add_failed): the add
  // genuinely did not complete, so it MUST be COUNTED toward blocking-spike alerts.
  // This is the deliberate contrast with S3/warn below — do not collapse the two.
  S2_ERROR: 'S2/error',
  // S3/warn marks an EXPECTED, non-failure signal: alert rules MUST EXCLUDE it
  // from blocking-spike semantics — for discovery_empty, "no cameras found yet"
  // is a normal early-journey state, not an incident.
  S3_WARN: 'S3/warn',
  /**
   * Informational, never a failure. Stamped on signals that record a NORMAL
   * product outcome a user is expected to hit — reaching a desktop-only feature
   * from the browser is a platform limitation the product guides them through,
   * not a defect. Alerting must exclude S4/info entirely: an S4/info spike means
   * "more people found the guidance", which is never an incident.
   */
  S4_INFO: 'S4/info'
} as const;

export type EventSeverity = (typeof EVENT_SEVERITY)[keyof typeof EVENT_SEVERITY];

/** Property names `exceptionCatalogTags` stamps; the renderer's COMMON_PROPERTIES spreads them. */
export const EXCEPTION_CATALOG_PROPERTIES = {
  // ENG-4312: severity classification carried on canonical events whose firing is
  // EXPECTED rather than a failure (e.g. discovery_empty), so alerting can filter
  // on the property instead of hard-coding event names.
  SEVERITY: 'severity',
  // ENG-4308: does this failure block the new-user happy path? Lets an alert rule
  // key on impact rather than on a hand-maintained list of event names.
  BLOCKING: 'blocking',
  // ENG-4308: the plain-English what/why pair carried on named exceptions, so a
  // failure is readable in a dashboard column without opening the stack trace.
  ERROR_WHAT: 'error_what',
  ERROR_WHY: 'error_why'
} as const;

/** Every Iris exception fingerprint is namespaced so PostHog can group on it. */
export const EXCEPTION_FINGERPRINT_NAMESPACE = 'studio';

/**
 * Subsystem segment of a fingerprint. This is the same value the filters derive
 * into the queryable `subsystem` property, so the two can never disagree.
 */
export const EXCEPTION_SUBSYSTEM = {
  AUTH: 'auth',
  BRIDGE: 'bridge',
  BROWSER: 'browser',
  CERTS: 'certs',
  LIVEKIT: 'livekit',
  REACT: 'react',
  STATE_SYNC: 'state-sync',
  WEBRTC: 'webrtc'
} as const;

export type ExceptionSubsystem = (typeof EXCEPTION_SUBSYSTEM)[keyof typeof EXCEPTION_SUBSYSTEM];

/** Issue segment of a fingerprint — the specific named failure within a subsystem. */
export const EXCEPTION_ISSUE = {
  ADD_ICE_CANDIDATE_CLOSED: 'add-ice-candidate-closed',
  AUTO_LINK_FAILED: 'auto-link-failed',
  EFFECT_UNMOUNTED_BEFORE_COMPLETE: 'effect-unmounted-before-complete',
  ELECTRON_ONLY_LOCAL_CONNECTION: 'electron-only-local-connection',
  METADATA_UPDATE_TIMEOUT: 'metadata-update-timeout',
  MISSING_REFRESH_TOKEN: 'missing-refresh-token',
  NEGOTIATION_TIMEOUT: 'negotiation-timeout',
  OPERATION_ABORTED: 'operation-aborted',
  PC_MANAGER_CLOSED: 'pc-manager-closed',
  PUBLISHER_CONNECTION_CLOSED: 'publisher-connection-closed',
  SIGNAL_CONNECTION_FAILED: 'signal-connection-failed',
  SYNCHRONIZATION_IN_PROGRESS: 'synchronization-in-progress',
  WINDOWS_STORE_NOT_WRITABLE: 'windows-store-not-writable'
} as const;

export type ExceptionIssue = (typeof EXCEPTION_ISSUE)[keyof typeof EXCEPTION_ISSUE];

/**
 * Composed fingerprint type. Every literal below is checked against this via
 * `satisfies`, so a typo in either segment fails type-checking rather than
 * silently minting a fingerprint no grouping rule or dashboard knows about.
 */
export type ExceptionFingerprint =
  `${typeof EXCEPTION_FINGERPRINT_NAMESPACE}:${ExceptionSubsystem}:${ExceptionIssue}`;

/**
 * The named failures. Values are the fingerprints the noise-filter rules assign;
 * app code and tests reference these constants instead of repeating the literal.
 */
export const EXCEPTION_FINGERPRINTS = {
  AUTH_MISSING_REFRESH_TOKEN: 'studio:auth:missing-refresh-token',
  BRIDGE_AUTO_LINK_FAILED: 'studio:bridge:auto-link-failed',
  BRIDGE_ELECTRON_ONLY_LOCAL_CONNECTION: 'studio:bridge:electron-only-local-connection',
  BROWSER_OPERATION_ABORTED: 'studio:browser:operation-aborted',
  CERTS_WINDOWS_STORE_NOT_WRITABLE: 'studio:certs:windows-store-not-writable',
  LIVEKIT_METADATA_UPDATE_TIMEOUT: 'studio:livekit:metadata-update-timeout',
  LIVEKIT_NEGOTIATION_TIMEOUT: 'studio:livekit:negotiation-timeout',
  LIVEKIT_PC_MANAGER_CLOSED: 'studio:livekit:pc-manager-closed',
  LIVEKIT_PUBLISHER_CONNECTION_CLOSED: 'studio:livekit:publisher-connection-closed',
  LIVEKIT_SIGNAL_CONNECTION_FAILED: 'studio:livekit:signal-connection-failed',
  REACT_EFFECT_UNMOUNTED_BEFORE_COMPLETE: 'studio:react:effect-unmounted-before-complete',
  STATE_SYNC_SYNCHRONIZATION_IN_PROGRESS: 'studio:state-sync:synchronization-in-progress',
  WEBRTC_ADD_ICE_CANDIDATE_CLOSED: 'studio:webrtc:add-ice-candidate-closed'
} as const satisfies Readonly<Record<string, ExceptionFingerprint>>;

/**
 * Whether the shipped app can actually deliver this failure to PostHog.
 *
 * A grouping rule forwards its match, so the row's tags land on a real event and
 * a query can see them. A suppression rule is checked FIRST and returns
 * `decision: 'suppress'` with no properties at all, after which the main process
 * returns outright — nothing is stamped and nothing is sent. The `verbose`
 * escape hatch that would forward suppressed matches is gated on the
 * `posthog-error-tracking-verbose` feature flag, which exists in neither PostHog
 * project, so it is dead in the shipped product.
 *
 * This distinction is declared per row (and asserted against the live rule lists
 * in both filters' test suites) because the catalog is what the alerting and
 * dashboard lanes read to decide which failures they can build a metric on.
 */
export const EXCEPTION_DELIVERY = {
  GROUPED: 'grouped',
  SUPPRESSED: 'suppressed'
} as const;

export type ExceptionDelivery = (typeof EXCEPTION_DELIVERY)[keyof typeof EXCEPTION_DELIVERY];

/** Fields every row carries regardless of whether the event can be delivered. */
interface ExceptionCatalogEntryFields {
  /** What happened, in one plain-English sentence fragment. Becomes the Issue title. */
  readonly what: string;
  /** Why it happened / what the app was doing. Becomes the Issue description. */
  readonly why: string;
  /**
   * S1/S2/S3 classification from the ENG-4300 stage map's severity shorthand.
   * Describes the failure's nature, so it stays meaningful even on a row the app
   * suppresses.
   */
  readonly severity: EventSeverity;
}

/** A row a grouping rule forwards, so its `blocking` tag is an observable signal. */
interface DeliveredExceptionCatalogEntry extends ExceptionCatalogEntryFields {
  readonly delivery: typeof EXCEPTION_DELIVERY.GROUPED;
  /**
   * Whether this failure blocks the new-user happy path. S3/warn rows are never
   * blocking — alert rules exclude them, per the stage map's severity rules.
   */
  readonly blocking: boolean;
}

/**
 * A row a suppression rule drops before the send boundary. `blocking` is pinned
 * to `false` because no query can ever observe this row: a consumer counting
 * `blocking = true` would be promised a failure that contributes zero forever.
 * Lifting the suppression is what makes a blocking claim here meaningful, and the
 * delivery declaration is asserted against the rule lists so that move forces the
 * tag to be re-decided rather than silently inherited.
 */
interface SuppressedExceptionCatalogEntry extends ExceptionCatalogEntryFields {
  readonly delivery: typeof EXCEPTION_DELIVERY.SUPPRESSED;
  readonly blocking: false;
}

/** One catalog row: the human explanation plus the tags Product filters on. */
export type ExceptionCatalogEntry =
  | DeliveredExceptionCatalogEntry
  | SuppressedExceptionCatalogEntry;

/**
 * The catalog. Severity and blocking come from the signed-off ENG-4300 audit
 * (`event-exception-catalog.md` §3, "Maps to product Issue → stage" + Severity);
 * the audit's `Sn/noise` rows are expected teardown, so they land on S3/warn and
 * are non-blocking. `what`/`why` reuse the exact strings the grouping rules
 * already displayed, so no PostHog Issue title changes meaning.
 *
 * This catalog deliberately does NOT stamp the 1-8 `step`: that enum's
 * single-source location is owned by the foundation layer (ENG-4303 decision
 * #7) and is not yet instrumented in the envelope. Adding a second stage-number
 * map here is exactly what the ticket forbids, so `step` rides through from the
 * envelope once the foundation stamps it.
 */
export const ONBOARDING_EXCEPTION_CATALOG = {
  // Stage 3 (sign-in). Auth0 refresh-token lookup fails and the user cannot
  // complete or resume sign-in.
  [EXCEPTION_FINGERPRINTS.AUTH_MISSING_REFRESH_TOKEN]: {
    what: 'Missing Refresh Token',
    why: 'Auth0 refresh-token lookup failed; audience is captured when available.',
    severity: EVENT_SEVERITY.S2_ERROR,
    delivery: EXCEPTION_DELIVERY.GROUPED,
    blocking: true
  },
  // Stage 4 (link bridge). The link-step IPC failure — distinct from the
  // post-link connection-health watchdog, which is out of the funnel.
  [EXCEPTION_FINGERPRINTS.BRIDGE_AUTO_LINK_FAILED]: {
    what: 'Bridge Auto-Linking Failed',
    why: 'Bridge auto-link IPC failed while the app tried to connect the local bridge.',
    severity: EVENT_SEVERITY.S2_ERROR,
    delivery: EXCEPTION_DELIVERY.GROUPED,
    blocking: true
  },
  // Standalone web has no Electron IPC, so the local-bridge guard throws by
  // design. Expected guidance, not a defect.
  [EXCEPTION_FINGERPRINTS.BRIDGE_ELECTRON_ONLY_LOCAL_CONNECTION]: {
    what: 'Local connection is desktop-only',
    why: 'The browser build reached the local-bridge guard, which only the desktop app can satisfy.',
    severity: EVENT_SEVERITY.S3_WARN,
    delivery: EXCEPTION_DELIVERY.SUPPRESSED,
    blocking: false
  },
  [EXCEPTION_FINGERPRINTS.BROWSER_OPERATION_ABORTED]: {
    what: 'Browser operation aborted',
    why: 'An in-flight browser operation was cancelled during navigation or teardown.',
    severity: EVENT_SEVERITY.S3_WARN,
    delivery: EXCEPTION_DELIVERY.SUPPRESSED,
    blocking: false
  },
  // Stage 2 (boot). Standard (non-admin) Windows installs cannot write the CA
  // bundle under Program Files, so startup dies before the app is usable.
  [EXCEPTION_FINGERPRINTS.CERTS_WINDOWS_STORE_NOT_WRITABLE]: {
    what: 'Windows certificate store not writable',
    why: 'Windows CA bundle generation tried to write roots.pem under a protected path.',
    severity: EVENT_SEVERITY.S1_FATAL,
    delivery: EXCEPTION_DELIVERY.GROUPED,
    blocking: true
  },
  // Stage 8 (first stream). The ENG-4300 audit calls this the highest-volume real
  // media failure, but the ENG-3879 noise rule suppresses it as expected teardown
  // and that suppression is what ships: the fingerprint has never once reached
  // either PostHog project (0 events / 180 days, staging 47358 and prod 70344).
  // It stays non-blocking because a blocking metric can never count it. Deciding
  // whether stage-8 metadata timeouts belong in the blocking set means lifting the
  // suppression, which is a volume decision this catalog does not own.
  [EXCEPTION_FINGERPRINTS.LIVEKIT_METADATA_UPDATE_TIMEOUT]: {
    what: 'LiveKit metadata update timed out',
    why: 'The request to update local participant metadata did not complete before the timeout.',
    severity: EVENT_SEVERITY.S2_ERROR,
    delivery: EXCEPTION_DELIVERY.SUPPRESSED,
    blocking: false
  },
  [EXCEPTION_FINGERPRINTS.LIVEKIT_NEGOTIATION_TIMEOUT]: {
    what: 'LiveKit negotiation timed out',
    why: 'LiveKit media negotiation did not complete before the timeout.',
    severity: EVENT_SEVERITY.S2_ERROR,
    delivery: EXCEPTION_DELIVERY.GROUPED,
    blocking: true
  },
  // Expected room-teardown noise: the peer connection is already closed.
  [EXCEPTION_FINGERPRINTS.LIVEKIT_PC_MANAGER_CLOSED]: {
    what: 'LiveKit peer connection already closed',
    why: 'A LiveKit call arrived after the peer-connection manager had shut down, which is normal room teardown.',
    severity: EVENT_SEVERITY.S3_WARN,
    delivery: EXCEPTION_DELIVERY.SUPPRESSED,
    blocking: false
  },
  [EXCEPTION_FINGERPRINTS.LIVEKIT_PUBLISHER_CONNECTION_CLOSED]: {
    what: 'LiveKit publisher connection closed',
    why: 'The publisher peer connection closed while LiveKit was starting media publish.',
    severity: EVENT_SEVERITY.S2_ERROR,
    delivery: EXCEPTION_DELIVERY.GROUPED,
    blocking: true
  },
  [EXCEPTION_FINGERPRINTS.LIVEKIT_SIGNAL_CONNECTION_FAILED]: {
    what: 'LiveKit signal connection failed',
    why: 'LiveKit could not reach the signaling service during connection setup.',
    severity: EVENT_SEVERITY.S2_ERROR,
    delivery: EXCEPTION_DELIVERY.GROUPED,
    blocking: true
  },
  [EXCEPTION_FINGERPRINTS.REACT_EFFECT_UNMOUNTED_BEFORE_COMPLETE]: {
    what: 'React effect unmounted before completing',
    why: 'A component unmounted while one of its effects was still running, which StrictMode makes routine.',
    severity: EVENT_SEVERITY.S3_WARN,
    delivery: EXCEPTION_DELIVERY.SUPPRESSED,
    blocking: false
  },
  [EXCEPTION_FINGERPRINTS.STATE_SYNC_SYNCHRONIZATION_IN_PROGRESS]: {
    what: 'State sync synchronization in progress',
    why: 'A second state-sync run started before the previous synchronization completed.',
    severity: EVENT_SEVERITY.S3_WARN,
    delivery: EXCEPTION_DELIVERY.GROUPED,
    blocking: false
  },
  [EXCEPTION_FINGERPRINTS.WEBRTC_ADD_ICE_CANDIDATE_CLOSED]: {
    what: 'WebRTC ICE candidate added after close',
    why: 'An ICE candidate arrived after the peer connection closed, which is normal teardown ordering.',
    severity: EVENT_SEVERITY.S3_WARN,
    delivery: EXCEPTION_DELIVERY.SUPPRESSED,
    blocking: false
  }
} as const satisfies Readonly<Partial<Record<ExceptionFingerprint, ExceptionCatalogEntry>>>;

/**
 * Look up a catalog row. Accepts `unknown` because the exception properties are
 * loosely typed at the send boundary and `$exception_fingerprint` is allowed to
 * be a string array; anything unrecognised returns undefined so an unclassified
 * exception still ships, just without the product tags.
 */
export function resolveExceptionCatalogEntry(
  fingerprint: unknown
): ExceptionCatalogEntry | undefined {
  const key = Array.isArray(fingerprint) ? fingerprint[0] : fingerprint;

  if (typeof key !== 'string') {
    return undefined;
  }

  const catalog: Readonly<Record<string, ExceptionCatalogEntry>> = ONBOARDING_EXCEPTION_CATALOG;

  return catalog[key];
}

/**
 * The property stamp for one named failure, or undefined when the fingerprint is
 * not in the catalog. Both filters spread this onto the outgoing `$exception`
 * properties; keeping the key names here (rather than at each call site) is what
 * stops the two pipelines from stamping different spellings.
 */
export function exceptionCatalogTags(fingerprint: unknown) {
  const entry = resolveExceptionCatalogEntry(fingerprint);

  if (!entry) {
    return undefined;
  }

  return {
    [EXCEPTION_CATALOG_PROPERTIES.ERROR_WHAT]: entry.what,
    [EXCEPTION_CATALOG_PROPERTIES.ERROR_WHY]: entry.why,
    [EXCEPTION_CATALOG_PROPERTIES.SEVERITY]: entry.severity,
    [EXCEPTION_CATALOG_PROPERTIES.BLOCKING]: entry.blocking
  };
}

/** Per rule: think-in-systems (ai.knowledge/global) — both filters read one list with .has(). */
export const GENERIC_EXCEPTION_TYPES: ReadonlySet<string> = new Set([
  'AggregateError',
  'DOMException',
  'Error',
  'EvalError',
  'RangeError',
  'ReferenceError',
  'RuntimeError',
  'StudioError',
  'SyntaxError',
  'TypeError',
  'URIError'
]);
